
import {NoTzDate} from "../../lib/DateUtils";
import {DisplayStyle, IconTypes} from '../../Enums/UiEnums';
import { iconClass,bootstrapTextClass } from '../../lib/StyleHelpers'; 
import {prettyCurrency} from '../../lib/Formatting/Number'; 
import {openWindowFromVue} from '../../lib/Models/CommonItemsModel';    

export const paymentTypeWarning = "You have selected a payment type that does not affect payments. Because refunds and transfers affect payment amounts and have the potential to impact provider allocation, we recommend that a payment type that affects payments should be utilized.";
export const transferTypeWarning = "You have selected a payment type that is different than the default. Because transfers may need to affect payment amounts in order to impact provider allocation, we recommend that you validate the payment type selected.";

export enum ClaimSimpleLists {
    PaymentMethod = "PaymentMethodListView",
    PaymentType = "PaymentTypeListView",
    RefundPaymentType = "PaymentTypeRefundListView",
    ClaimStatus = "ClaimStatusListView",
    ClaimBilling = "ClaimBillingListView",
    ClaimLevel = "ClaimLevelListView",
    RespParty = "PatientResponsiblePartyListView",
    CreditCard = "PatientCreditCardView",
    PrimaryPayers = "ClaimPrimaryListView",
    RenderingProviders = "ClaimRenderingListView",
    ServiceLocations = "ClaimServiceListView",
    FinancialClass= "FinancialClassView", 
}

export enum ClaimSelectionType {
    Included = 1,
    Patient = 2
}
export enum ClaimDisplay {
    Full = 1,
    CreditHeader = 2,
    CreditPayment = 3,
    Refund = 4,
    Transfer = 5,
    RefundTransfer = 6,
    PaymentPlan = 7,
    PaymentPlanIncluded = 8,
    PaymentPlanDetails = 9
};
export enum RefundArea {
    ResponsibleParty = "R",
    PrimaryInsurance = "1",
    SecondaryInsurance = "2",
    TertiaryInsurance = "3",
    Unknown = "U",
    Missing = "M"
}
export enum ClaimMenuActions{
    Procedures,
    Payments,
    Comments 
} 
export enum RefundMenuActions {
    Break,
    ResponsibleParty,
    PrimaryInsurance,
    SecondaryInsurance,
    TertiaryInsurance,
    Cancel
} 
export enum RefundMenuType {
    Break,
    Cancel,
    Reassign
}
export interface IRefundReassignment {
    ClaimID: number
    ReassignmentSourceID: RefundArea,
    ReassignmentActionID: RefundMenuActions,
    Amount: number
}

export interface IBillingResponse {
    errorCode: number
    errorMessage: string 
}
export interface IClaim {
    claimID: number 
}; 
export interface IClaimEntity {
    entityID: number 
};
export interface IClaimList {
    claimID: number,
    selected: boolean 
}; 
export interface IClaimComment {
    commentID: number,
    enterDate: NoTzDate,
    userID: string, 
    comment: string, 
    diagnosisID: number,
};
export interface IClaimHeader {
    claimID: number,
    dos: NoTzDate,
    levelID: string, 
    levelDescription: string, 
    statusID: number, 
    statusDescription: string, 
    charges: number, 
    payments: number, 
    adjustments: number, 
    claimBalance: number, 
    creditBalance: number, 
    openBalance: number, 
    patientBalance: number, 
    renderingProviderEID: number,
    renderingProviderName: string,
    primaryPayerEID: number, 
    primaryPayerName: string, 
    primarySelfPay: number,
    secondaryPayerEID: number,
    secondaryPayerName: string,
    tertiaryPayerEID: number,
    tertiaryPayerName: string,
    responsiblePartyEID: number,
    responsiblePartyName: string,
    serviceLocationEID: number,
    serviceLocationName: string
}; 
export interface IClaimPayment {
    claimID: number,
    diagnosisID: number,
    diagnosisLine: number,
    procedureCode: string,
    paymentID: number, 
    paymentDate: NoTzDate, 
    postDate: NoTzDate,
    payerName: string, 
    paymentMethodDescription: string, 
    paymentTypeDescription: string, 
    checkNumber: string, 
    paymentAmount: number, 
    adjustmentAmount: number, 
    paymentReversed: number,
    commentIndicator: string
};
export interface IClaimProcedureIds {
    claimID: number,
    diagnosisID: number,
    diagnosisLine: number,
    procedureCode: string
}; 
export interface IClaimProcedure {
    claimID: number,
    diagnosisID: number,
    diagnosisLine: number,
    fromDate: NoTzDate, 
    toDate: NoTzDate, 
    procedureCode: string, 
    procedureDescription: string,
    units: string,
    amount: number, 
    unitCharge: number, 
    payments: number, 
    adjustments: number, 
    modifiers: string,
    transferAmount?: number,
    balance?: number
};
export interface IClaimTableFormat {
    inputDisplay: boolean, 
    inputLabel: string,
    levelDisplay: boolean, 
    statusDisplay: boolean, 
    payerDisplay: boolean, 
    columns: number, 
    tableWidth: number
};
export interface IClaimEntityRefunds {
    refundTotal: number, 
    refundEID: number,
    refundArea: RefundArea, 
    payerType: string, 
    payerName: string    
};
export interface IRefundHeaders {
    refundEID: number,
    payerType: string, 
    payerName: string,
    maxRefund: number,
    selectedRefund: number,
    paymentType: string, 
};
export interface IRefundReturns {
    claimID: number,
    refundEID: number, 
    refundArea: RefundArea,
    refundAmount: number,
    paymentType: number
};

export interface IRefundReturnRequest {
    notes?: string,
    refundReturnList: IRefundReturns[]
};

export interface ITransferReturns {
    claimID: number,
    diagnosisID: number, 
    allocatedAmount: number 
};
export interface ITransferReturnRequest {
    paymentMethodID: string,
    paymentMethodTypeID: string,
    notes?: string,
    refundReturnList: IRefundReturns[],
    transferReturnList: ITransferReturns[]
};
export interface IUpdatedClaimEntityRefunds extends IClaimEntityRefunds {
    newRefundTotal: number,
    claimID: number
}
export const ClaimColumnDefinitions = 
{
    ClaimID: 4.7, 
    DOS: 4.375,
    Level: 4.06,
    Status: 10.935,
    Payer: 8.75,
    Charges: 4.7, 
    Payments: 4.7, 
    Adjustments: 4.7, 
    Balance: 4.7, 
    Input: 6.875
};

export const ClaimTableWidth = 
    ClaimColumnDefinitions.ClaimID + 
    ClaimColumnDefinitions.DOS + 
    ClaimColumnDefinitions.Charges + 
    ClaimColumnDefinitions.Payments +
    ClaimColumnDefinitions.Adjustments +
    ClaimColumnDefinitions.Balance;

export const ClaimColumnWidth = .5;
export const RefundMenuExplanation = "Selecting one of the items above will adjust the refund accordingly.";


export class RefundTypeList {
    area: RefundArea;
    description: string;
    icon: IconTypes;
    style: DisplayStyle;
    
    constructor(area: RefundArea, description: string, icon: IconTypes, style: DisplayStyle) {
        this.area = area;
        this.description = description;
        this.icon = icon;
        this.style = style; 
    }
  
    get iconClassStyle() : string {
       return `${iconClass(this.icon)} ${bootstrapTextClass(this.style)}`;
    }

    iconTooltip(payerName : string, creditAmount : number,payerEID? :number, ) : string {
        return `${this.description}\r\nName: ${payerName}\r\nCredit Amount: ${prettyCurrency(creditAmount)}${(payerEID ? "\r\nID: " + payerEID : "")}`;
    }
    iconUnaffiliatedTooltip(unaffiliatedRefunds : IClaimEntityRefunds[]) : string {
        const unaffiliated_data = unaffiliatedRefunds.map(item => `${item.payerName} / ${prettyCurrency(item.refundTotal)}`).flat().join("\r\n");
        return `${this.description}\r\n${unaffiliated_data}`;
    }
}

export const refundRecordTypes : Record<RefundArea, RefundTypeList> ={
    [RefundArea.ResponsibleParty] : new  RefundTypeList (RefundArea.ResponsibleParty,"Responsible Party",IconTypes.User,DisplayStyle.Primary),
    [RefundArea.PrimaryInsurance] : new  RefundTypeList (RefundArea.PrimaryInsurance,"Primary Insurance",IconTypes.PrimaryInsurance,DisplayStyle.Primary),
    [RefundArea.SecondaryInsurance] : new  RefundTypeList (RefundArea.SecondaryInsurance,"Secondary Insurance",IconTypes.SecondaryInsurance,DisplayStyle.Primary),
    [RefundArea.TertiaryInsurance] : new  RefundTypeList (RefundArea.TertiaryInsurance,"Tertiary Insurance",IconTypes.TertiaryInsurance,DisplayStyle.Primary),
    [RefundArea.Missing] : new  RefundTypeList (RefundArea.Missing,"Unassigned",IconTypes.Unknown,DisplayStyle.Warning),
    [RefundArea.Unknown] : new  RefundTypeList (RefundArea.Unknown,"Unaffiliated",IconTypes.Error,DisplayStyle.Danger),
}
 
export function amountClass(inputAmount : number | undefined, reversal : number,muted: boolean ) : string {
    return "pe-2 " + ((inputAmount ?? 0) < 0 ? " isalus-negative-number" : " billing-data-right") + (reversal === 1 ? " billing-reversal" : "") + (muted ? " text-muted" : "");
};

// This is a call to window.js, will eventually need to be fixed..
export function claimLegacyWindowOpen(windowID: string, windowURL: string) : void {
    openWindowFromVue(windowID, windowURL,{ noTimestamp: "N" });
}; 